import { store, observable, action, computed } from '~/common/mobx.decorator'
import { apolloClient, GET_HOT_JOBS_QUERY } from '~/common/apollo'
import { LIMIT_PAGE, SORT_TYPE } from '~/common/constants'
import { logDefaultActionEvent } from '~/common/tracking/event-client.tracking'
import { EVENT_DEFAULT_ID, TYPE_JOB } from '~/common/tracking/event-click.constant'
import { authStore } from '~/stores'

@store()
class GlobalSearchStore {
	@observable jobs = []
	@observable totalCount
	@observable pageInfo = {}
	@observable order = SORT_TYPE.desc
	@observable field = 'SCORE'
	@observable keyword = ''

	@computed
	get jobsItems() {
		return this.jobs
	}

	@computed
	get hasNextPage() {
		return this.pageInfo?.hasNextPage
	}

	@computed
	get endCursor() {
		return this.pageInfo?.endCursor
	}

	@action
	setKeyword = (keyword) => {
		this.keyword = keyword
	}

	@action
	fetchJobs = async () => {
		if (!this.keyword) {
			this.jobs = []
			this.totalCount = 0
			this.pageInfo = {}
			return
		}
		const response = await apolloClient.query({
			query: GET_HOT_JOBS_QUERY,
			variables: {
				filter: { keyword: this.keyword },
				first: LIMIT_PAGE,
				sortBy: {
					field: this.field,
					order: this.order,
				},
			},
			context: {
				clientName: authStore.id ? null : 'public',
			},
		})
		this.jobs = response.data.searchJobs?.nodes
		this.totalCount = response.data.searchJobs?.totalCount
		this.pageInfo = response.data.searchJobs?.pageInfo

		logDefaultActionEvent(EVENT_DEFAULT_ID.loadInitialLanding, {
			jobs: this.jobs?.map((x, index) => {
				return {
					id: x?.id,
					min_wage: x?.payAmountMin,
					max_wage: x?.payAmountMax,
					order: index,
					percent_matched: x?.matchingPercentage,
				}
			}),
			page_number: this.totalCount === 0 ? 1 : Math.ceil(this.jobs?.length / 20) + 1,
			limit: 20,
			list_type: TYPE_JOB.hotJobs,
		})
	}

	@action
	onScrollToEnd = async () => {
		const response = await apolloClient.query({
			query: GET_HOT_JOBS_QUERY,
			variables: {
				filter: { keyword: this.keyword },
				first: LIMIT_PAGE,
				sortBy: {
					field: this.field,
					order: this.order,
				},
				after: this.endCursor,
			},
			context: {
				clientName: authStore.id ? null : 'public',
			},
		})

		this.jobs = [...this.jobs, ...response?.data?.searchJobs?.nodes]
		this.pageInfo = response.data.searchJobs?.pageInfo

		logDefaultActionEvent(EVENT_DEFAULT_ID.loadMoreLanding, {
			jobs: this.jobs?.map((x, index) => {
				return {
					id: x.id,
					min_wage: x?.payAmountMin,
					max_wage: x?.payAmountMax,
					order: index,
					percent_matched: x?.matchingPercentage,
				}
			}),
			page_number: this.totalCount === 0 ? 1 : Math.ceil(this.jobs?.length / 20) + 1,
			limit: 20,
			offset: this.jobs?.length || 0,
			list_type: TYPE_JOB.hotJobs,
		})
	}
	@action
	onSort = async (field) => {
		const response = await apolloClient.query({
			query: GET_HOT_JOBS_QUERY,
			variables: {
				first: LIMIT_PAGE,
				filter: { keyword: this.keyword },
				sortBy: {
					field: field,
					order: this.order,
				},
			},
		})
		this.field = field
		this.jobs = response.data.searchJobs?.nodes
		this.pageInfo = response.data.searchJobs?.pageInfo
	}

	@action
	reset = () => {
		this.jobs = []
		this.totalCount = 0
		this.pageInfo = {}
		this.keyword = ''
	}
}

export const globalSearchStore = new GlobalSearchStore()
